import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  Users,
  Eye,
  Pencil,
  Trash2,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

export default function GroupsPage() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);

  // Edit modal
  const [editGroup, setEditGroup] = useState(null);
  const [editName, setEditName] = useState('');
  const [editing, setEditing] = useState(false);

  // Delete confirm
  const [deleteGroup, setDeleteGroup] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const data = await api.getGroups();
      setGroups(data || []);
    } catch (e) {
      toast.error(e?.message || 'Error al cargar grupos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    const name = createName.trim();
    if (!name) {
      toast.error('Ingresá un nombre');
      return;
    }
    setCreating(true);
    try {
      const created = await api.createGroup(name);
      setGroups((prev) => [created, ...prev]);
      toast.success('Grupo creado');
      setCreateOpen(false);
      setCreateName('');
    } catch (e) {
      toast.error(e?.message || 'No se pudo crear el grupo');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (g) => {
    setEditGroup(g);
    setEditName(g.name);
  };

  const handleEdit = async () => {
    const name = editName.trim();
    if (!name || !editGroup) return;
    if (name === editGroup.name) {
      setEditGroup(null);
      return;
    }
    setEditing(true);
    try {
      const updated = await api.updateGroup(editGroup.id, name);
      setGroups((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
      toast.success('Grupo actualizado');
      setEditGroup(null);
    } catch (e) {
      toast.error(e?.message || 'No se pudo actualizar el grupo');
    } finally {
      setEditing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteGroup) return;
    setDeleting(true);
    try {
      const res = await api.deleteGroup(deleteGroup.id);
      setGroups((prev) => prev.filter((g) => g.id !== deleteGroup.id));
      const unassigned = res?.students_unassigned ?? 0;
      toast.success(
        unassigned > 0
          ? `Grupo eliminado. ${unassigned} alumno(s) quedaron sin grupo.`
          : 'Grupo eliminado.'
      );
      setDeleteGroup(null);
    } catch (e) {
      toast.error(e?.message || 'No se pudo eliminar el grupo');
    } finally {
      setDeleting(false);
    }
  };

  const viewStudents = (g) => {
    navigate(`/evaluations?group_id=${encodeURIComponent(g.id)}`);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="groups-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 font-['Inter']">Grupos</h1>
            <p className="text-gray-500 text-sm mt-1 font-['Inter']">
              Agrupá a tus alumnos por equipos, gimnasios o categorías.
            </p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-[#1F3A5F] hover:bg-[#2a4a73] font-['Inter']"
            data-testid="create-group-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Crear Grupo
          </Button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="h-8 w-8 animate-spin text-[#1F3A5F]" />
          </div>
        ) : groups.length === 0 ? (
          <Card className="border-gray-200 bg-white">
            <CardContent className="py-16">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-gray-100 mx-auto mb-4 flex items-center justify-center">
                  <Users className="h-7 w-7 text-gray-400" />
                </div>
                <h3 className="font-medium text-gray-700 mb-1 font-['Inter']">
                  Aún no creaste grupos
                </h3>
                <p className="text-sm text-gray-500 mb-4 font-['Inter']">
                  Creá un grupo (ej: "Sub-18 Vóley", "Gimnasio Montoto") y luego asignalo en cada nueva entrevista.
                </p>
                <Button
                  onClick={() => setCreateOpen(true)}
                  size="sm"
                  className="bg-[#1F3A5F] hover:bg-[#2a4a73] font-['Inter']"
                  data-testid="create-first-group-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Crear primer grupo
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {groups.map((g) => (
              <Card
                key={g.id}
                className="border-gray-200 bg-white hover:shadow-md transition-shadow"
                data-testid={`group-card-${g.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-full bg-[#1F3A5F]/10 flex items-center justify-center flex-shrink-0">
                        <Users className="h-5 w-5 text-[#1F3A5F]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800 font-['Inter']" data-testid={`group-name-${g.id}`}>
                          {g.name}
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5 font-['Inter']">
                          {g.students_count} {g.students_count === 1 ? 'alumno' : 'alumnos'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 ml-15 md:ml-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewStudents(g)}
                        className="border-gray-200 text-gray-700 hover:text-[#1F3A5F] font-['Inter']"
                        data-testid={`view-students-${g.id}`}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Ver alumnos
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(g)}
                        className="text-gray-400 hover:text-gray-700"
                        data-testid={`edit-group-${g.id}`}
                        aria-label="Editar nombre"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteGroup(g)}
                        className="text-gray-400 hover:text-red-600"
                        data-testid={`delete-group-${g.id}`}
                        aria-label="Eliminar grupo"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Modal */}
        <Dialog open={createOpen} onOpenChange={(v) => { if (!creating) { setCreateOpen(v); if (!v) setCreateName(''); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-['Inter']">Crear grupo</DialogTitle>
              <DialogDescription className="font-['Inter']">
                Asignale un nombre claro (ej: "Sub-18 Vóley", "Gimnasio Montoto").
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="new-group-name" className="font-['Inter']">Nombre del grupo</Label>
              <Input
                id="new-group-name"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                placeholder="Ej: Sub-18 Vóley"
                autoFocus
                maxLength={80}
                className="font-['Inter']"
                data-testid="input-new-group-name"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setCreateOpen(false); setCreateName(''); }}
                disabled={creating}
                className="font-['Inter']"
                data-testid="cancel-create-group"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreate}
                disabled={creating || !createName.trim()}
                className="bg-[#1F3A5F] hover:bg-[#2a4a73] font-['Inter']"
                data-testid="confirm-create-group"
              >
                {creating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creando…</>) : 'Crear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={!!editGroup} onOpenChange={(v) => { if (!v && !editing) setEditGroup(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-['Inter']">Editar grupo</DialogTitle>
              <DialogDescription className="font-['Inter']">
                Cambiá el nombre del grupo.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="edit-group-name" className="font-['Inter']">Nombre del grupo</Label>
              <Input
                id="edit-group-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleEdit(); }}
                autoFocus
                maxLength={80}
                className="font-['Inter']"
                data-testid="input-edit-group-name"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditGroup(null)}
                disabled={editing}
                className="font-['Inter']"
                data-testid="cancel-edit-group"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleEdit}
                disabled={editing || !editName.trim()}
                className="bg-[#1F3A5F] hover:bg-[#2a4a73] font-['Inter']"
                data-testid="confirm-edit-group"
              >
                {editing ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Guardando…</>) : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteGroup} onOpenChange={(v) => { if (!v && !deleting) setDeleteGroup(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-['Inter']">¿Eliminar grupo?</AlertDialogTitle>
              <AlertDialogDescription className="font-['Inter']">
                {deleteGroup?.students_count > 0 ? (
                  <>El grupo <strong>{deleteGroup?.name}</strong> tiene <strong>{deleteGroup?.students_count}</strong> {deleteGroup?.students_count === 1 ? 'alumno' : 'alumnos'} asignado(s). No se borrarán las entrevistas — quedarán como "Sin grupo".</>
                ) : (
                  <>Se eliminará el grupo <strong>{deleteGroup?.name}</strong>. Esta acción no se puede deshacer.</>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                className="border-gray-200 font-['Inter']"
                disabled={deleting}
                data-testid="cancel-delete-group"
              >
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white font-['Inter']"
                data-testid="confirm-delete-group"
              >
                {deleting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Eliminando…</>) : 'Eliminar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
